import { z } from 'zod';
import { Classification } from './classification.js';

/** `with` of an `approval` step (ADR-031). */
export const ApprovalStepParams = z.object({
  title: z.string().min(1).max(200),
  /** What the reviewer must check before deciding. */
  instructions: z.string().max(2000).default(''),
  /** What is being decided on, usually `${steps.<key>.output...}` (bounded when stored). */
  subject: z.record(z.string(), z.unknown()).default({}),
  /** `stop` ends the execution (outcome "stopped") on rejection; `continue` lets later steps branch. */
  onReject: z.enum(['stop', 'continue']).default('stop'),
});
export type ApprovalStepParams = z.infer<typeof ApprovalStepParams>;

export const ApprovalStatus = z.enum(['pending', 'approved', 'rejected']);
export type ApprovalStatus = z.infer<typeof ApprovalStatus>;

export const ApprovalView = z.object({
  id: z.string().uuid(),
  executionId: z.string().uuid(),
  workflow: z.object({ key: z.string(), version: z.number().int() }),
  stepKey: z.string(),
  title: z.string(),
  instructions: z.string(),
  subject: z.record(z.string(), z.unknown()),
  classification: Classification,
  status: ApprovalStatus,
  requestedBy: z.object({ userId: z.string().uuid(), email: z.string().nullable() }),
  note: z.string().nullable(),
  decidedBy: z.object({ userId: z.string().uuid(), email: z.string().nullable() }).nullable(),
  decidedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type ApprovalView = z.infer<typeof ApprovalView>;

export const ApprovalList = z.object({
  items: z.array(ApprovalView),
  nextCursor: z.string().nullable(),
});
export type ApprovalList = z.infer<typeof ApprovalList>;

export const ApprovalDecision = z.object({
  decision: z.enum(['approve', 'reject']),
  note: z.string().max(2000).optional(),
});
export type ApprovalDecision = z.infer<typeof ApprovalDecision>;
